import type { ReactNode } from 'react'
import { Button } from './Button'
import { useNls } from '../../nls/nlsProvider'
import './settingsKit.css'

export interface SettingsSaveBarProps {
  dirty: boolean
  saving?: boolean
  saveDisabled?: boolean
  onSave?: () => void | Promise<void>
  onDiscard?: () => void
  message?: ReactNode
  saveLabel?: ReactNode
  discardLabel?: ReactNode
  className?: string
  testId?: string
}

/**
 * SettingsSaveBar (SR5, §3.3)
 * Sticky bar at the bottom of the content column for draft settings.
 * Hidden when dirty is false.
 */
export function SettingsSaveBar({
  dirty,
  saving = false,
  saveDisabled = false,
  onSave,
  onDiscard,
  message,
  saveLabel,
  discardLabel,
  className,
  testId,
}: SettingsSaveBarProps) {
  const { t } = useNls()

  if (!dirty) {
    return null
  }

  return (
    <aside
      className={['skSaveBar', className].filter(Boolean).join(' ')}
      data-testid={testId}
      aria-label={t('NLSSettingsKitUnsavedChanges')}
    >
      <span className="skSaveBar__message">
        {message ?? t('NLSSettingsKitUnsavedChanges')}
      </span>
      <div className="skSaveBar__actions">
        <Button
          variant="tertiary"
          size="sm"
          disabled={saving}
          onClick={onDiscard}
        >
          {discardLabel ?? t('NLSSettingsKitDiscard')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          loading={saving}
          disabled={saving || saveDisabled}
          onClick={saveDisabled ? undefined : onSave}
        >
          {saveLabel ?? t('NLSSettingsKitSave')}
        </Button>
      </div>
    </aside>
  )
}
